import React, { useEffect, useState } from "react";
import { Scale, User, Ruler, Calendar, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import client, { formatErr } from "../lib/api";

const BMI_COLORS = {
    Underweight: "text-sky-300",
    Normal: "text-neon-green",
    Overweight: "text-yellow-400",
    Obese: "text-red-400",
};

export default function BodyMetricsCard() {
    const [metrics, setMetrics] = useState(null);
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({ age: "", sex: "", weight_kg: "", height_cm: "" });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const r = await client.get("/users/me/body-metrics");
            setMetrics(r.data);
            setForm({
                age: r.data.age ?? "",
                sex: r.data.sex ?? "",
                weight_kg: r.data.weight_kg ?? "",
                height_cm: r.data.height_cm ?? "",
            });
        } catch (e) {
            // non-fatal
        }
    };

    useEffect(() => {
        load();
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            const payload = {};
            if (form.age !== "") payload.age = Number(form.age);
            if (form.sex) payload.sex = form.sex;
            if (form.weight_kg !== "") payload.weight_kg = Number(form.weight_kg);
            if (form.height_cm !== "") payload.height_cm = Number(form.height_cm);
            const r = await client.put("/users/me/body-metrics", payload);
            setMetrics(r.data);
            setEditing(false);
            toast.success("Body metrics saved");
        } catch (e) {
            toast.error(formatErr(e));
        } finally {
            setSaving(false);
        }
    };

    if (!metrics) return null;

    const bmiColor = BMI_COLORS[metrics.bmi_category] || "text-zinc-400";
    const hasMetrics = metrics.weight_kg || metrics.height_cm || metrics.age;

    return (
        <div className="surface-2 p-4 mt-3" data-testid="body-metrics-card">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Scale size={16} className="text-neon-green" />
                    <h2 className="font-display font-black uppercase tracking-widest text-sm">Body Metrics</h2>
                </div>
                {!editing ? (
                    <button
                        onClick={() => setEditing(true)}
                        data-testid="body-metrics-edit"
                        className="text-xs text-neon-green label-cap flex items-center gap-1"
                    >
                        <Pencil size={11} /> {hasMetrics ? "Edit" : "Add"}
                    </button>
                ) : (
                    <div className="flex gap-2">
                        <button
                            onClick={() => setEditing(false)}
                            data-testid="body-metrics-cancel"
                            className="text-xs text-zinc-500 flex items-center gap-1"
                        >
                            <X size={12} /> Cancel
                        </button>
                        <button
                            onClick={save}
                            disabled={saving}
                            data-testid="body-metrics-save"
                            className="text-xs text-neon-orange label-cap flex items-center gap-1 disabled:opacity-50"
                        >
                            <Check size={12} /> Save
                        </button>
                    </div>
                )}
            </div>

            {editing ? (
                <div className="mt-4 grid grid-cols-2 gap-3">
                    <Field label="Age" icon={Calendar}>
                        <input
                            type="number"
                            inputMode="numeric"
                            value={form.age}
                            onChange={(e) => setForm({ ...form, age: e.target.value })}
                            placeholder="e.g. 28"
                            data-testid="bm-input-age"
                            className="w-full bg-charcoal-900 border border-charcoal-500 rounded px-2 py-1.5 text-sm focus:border-neon-green outline-none"
                        />
                    </Field>
                    <Field label="Sex" icon={User}>
                        <select
                            value={form.sex}
                            onChange={(e) => setForm({ ...form, sex: e.target.value })}
                            data-testid="bm-input-sex"
                            className="w-full bg-charcoal-900 border border-charcoal-500 rounded px-2 py-1.5 text-sm focus:border-neon-green outline-none"
                        >
                            <option value="">—</option>
                            <option value="m">Male</option>
                            <option value="f">Female</option>
                            <option value="other">Other</option>
                        </select>
                    </Field>
                    <Field label="Weight (kg)" icon={Scale}>
                        <input
                            type="number"
                            step="0.1"
                            inputMode="decimal"
                            value={form.weight_kg}
                            onChange={(e) => setForm({ ...form, weight_kg: e.target.value })}
                            placeholder="e.g. 75.5"
                            data-testid="bm-input-weight"
                            className="w-full bg-charcoal-900 border border-charcoal-500 rounded px-2 py-1.5 text-sm focus:border-neon-green outline-none"
                        />
                    </Field>
                    <Field label="Height (cm)" icon={Ruler}>
                        <input
                            type="number"
                            step="0.5"
                            inputMode="decimal"
                            value={form.height_cm}
                            onChange={(e) => setForm({ ...form, height_cm: e.target.value })}
                            placeholder="e.g. 178"
                            data-testid="bm-input-height"
                            className="w-full bg-charcoal-900 border border-charcoal-500 rounded px-2 py-1.5 text-sm focus:border-neon-green outline-none"
                        />
                    </Field>
                </div>
            ) : (
                <>
                    {!hasMetrics ? (
                        <p className="text-zinc-500 text-xs mt-2">
                            Add your age, sex, weight and height to calculate BMI and tailor your calorie burn estimates.
                        </p>
                    ) : (
                        <div className="mt-3 grid grid-cols-4 gap-2 text-center">
                            <Display label="Age" value={metrics.age || "—"} testid="bm-age" />
                            <Display label="Sex" value={metrics.sex ? metrics.sex.toUpperCase() : "—"} testid="bm-sex" />
                            <Display label="kg" value={metrics.weight_kg ?? "—"} testid="bm-weight" />
                            <Display label="cm" value={metrics.height_cm ?? "—"} testid="bm-height" />
                        </div>
                    )}
                    {metrics.bmi != null && (
                        <div className="mt-4 surface-1 p-3 flex items-center justify-between" data-testid="bm-bmi-display">
                            <div>
                                <div className="label-cap text-[10px] text-zinc-500">BMI</div>
                                <div className={`font-display font-black text-3xl ${bmiColor} leading-none mt-1`} data-testid="bm-bmi-value">
                                    {metrics.bmi}
                                </div>
                            </div>
                            <div className={`label-cap text-xs ${bmiColor}`} data-testid="bm-bmi-category">
                                {metrics.bmi_category}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function Field({ label, icon: Icon, children }) {
    return (
        <label className="block">
            <span className="label-cap text-[10px] text-zinc-500 flex items-center gap-1 mb-1">
                {Icon && <Icon size={10} />} {label}
            </span>
            {children}
        </label>
    );
}

function Display({ label, value, testid }) {
    return (
        <div className="surface-1 p-2" data-testid={testid}>
            <div className="font-display font-black text-lg leading-none">{value}</div>
            <div className="label-cap text-[10px] text-zinc-500 mt-1">{label}</div>
        </div>
    );
}
